// The 3D scene: a three.js renderer, the lights, one loaded model group, its
// part binder, its cutaway and the motion display. It knows nothing about the
// app shell; register.ts (the stage) and the dev page (web/dev/scene.html)
// drive it.
//
// Each frame: frame(heard, omega, now) picks the angle to draw (display-clock.ts),
// samples the pose there (pose.ts), sets the joints (binder.ts), places the caps
// (cutaway.ts) and renders.

import {
  ACESFilmicToneMapping,
  Box3,
  Color,
  DirectionalLight,
  HemisphereLight,
  PerspectiveCamera,
  PMREMGenerator,
  Raycaster,
  Scene,
  SRGBColorSpace,
  Vector2,
  Vector3,
  WebGLRenderer,
  type Mesh,
  type MeshStandardMaterial,
  type Object3D,
  type Texture,
} from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { RoomEnvironment } from "three/addons/environments/RoomEnvironment.js";

import { PartBinder } from "./binder";
import { Cutaway, findParts, type CapMode, type CutPlaneInput, type PickResult, type ScenePart } from "./cutaway";
import { DisplayClock, type DisplayMode } from "./display-clock";
import { type PoseSource } from "./pose";

/** A camera as the view state holds it: metres in the engine frame, vertical fov in degrees. */
export interface CameraPose {
  position: readonly number[];
  target: readonly number[];
  fov: number;
}

export interface EngineSceneOptions {
  canvas: HTMLCanvasElement;
  /** "stencil" (the default where the context has a stencil buffer), "backface" or "none". */
  caps?: CapMode | undefined;
  display?: DisplayMode;
  /** Keep the drawing buffer after presenting, so tests can read pixels. */
  preserveDrawingBuffer?: boolean;
  /** Multisampling; by default on, except on a software renderer, where it doubles the cost of a frame. */
  antialias?: boolean | undefined;
  /** Device pixel ratio cap; default min(devicePixelRatio, 2). */
  pixelRatio?: number;
  background?: number;
  /**
   * "studio" lights metals with a reflected room (image-based lighting); "simple"
   * leaves it out and tones metals down, which costs a quarter as much on a
   * software renderer. "auto" (the default) takes simple on a software renderer
   * (SwiftShader, llvmpipe, WARP), which browsers fall back to without a GPU.
   */
  lighting?: "auto" | "studio" | "simple" | undefined;
}

/** Renderer names of the software rasterisers browsers fall back to. */
const SOFTWARE_RENDERER = /swiftshader|llvmpipe|softpipe|software|microsoft basic render/i;
/** In simple lighting, metals reflect nothing, so they keep this much metalness and show their colour. */
const SIMPLE_METALNESS = 0.3;

export interface LoadedModel {
  readonly root: Object3D;
  readonly parts: readonly ScenePart[];
  readonly binder: PartBinder;
  readonly cutaway: Cutaway;
  readonly pose: PoseSource;
  /** World bounds at rest. */
  readonly bounds: Box3;
}

export interface FrameInfo {
  /** The crank angle drawn, radians in [0, 4π). */
  angle: number;
  /** Draw calls and triangles of the frame. */
  calls: number;
  triangles: number;
  caps: number;
}

export const BACKGROUND = 0x1c2027;

const _dir = new Vector3();

/** The name of the renderer a WebGL2 context gets here, from a throwaway context ("" without WebGL2). */
function probeRenderer(): string {
  const gl = document.createElement("canvas").getContext("webgl2");
  if (!gl) return "";
  const info = gl.getExtension("WEBGL_debug_renderer_info");
  const name = String(gl.getParameter(info ? info.UNMASKED_RENDERER_WEBGL : gl.RENDERER));
  gl.getExtension("WEBGL_lose_context")?.loseContext();
  return name;
}

export class EngineScene {
  readonly renderer: WebGLRenderer;
  readonly scene = new Scene();
  readonly camera = new PerspectiveCamera(30, 16 / 9, 0.01, 50);
  readonly clock: DisplayClock;
  readonly capMode: CapMode;
  readonly lighting: "studio" | "simple";
  /** The WebGL renderer's name, such as "ANGLE (…SwiftShader…)". */
  readonly gpu: string;
  model: LoadedModel | null = null;
  private values = new Float64Array(0);
  private lastNow = Number.NaN;
  private readonly raycaster = new Raycaster();
  private readonly target = new Vector3();
  private readonly environment: Texture | null = null;
  private readonly key = new DirectionalLight(0xffffff, 1.8);
  private readonly fill = new DirectionalLight(0xc8d4ff, 0.6);

  constructor(options: EngineSceneOptions) {
    const software = SOFTWARE_RENDERER.test(probeRenderer());
    const renderer = new WebGLRenderer({
      canvas: options.canvas,
      antialias: options.antialias ?? !software,
      stencil: true,
      preserveDrawingBuffer: options.preserveDrawingBuffer ?? false,
      powerPreference: "high-performance",
    });
    renderer.setPixelRatio(options.pixelRatio ?? Math.min(globalThis.devicePixelRatio || 1, 2));
    renderer.setClearColor(options.background ?? BACKGROUND);
    renderer.localClippingEnabled = true;
    renderer.outputColorSpace = SRGBColorSpace;
    renderer.toneMapping = ACESFilmicToneMapping;
    this.renderer = renderer;
    const hasStencil = renderer.getContext().getContextAttributes()?.stencil === true;
    this.capMode = options.caps ?? (hasStencil ? "stencil" : "backface");
    if (this.capMode === "stencil" && !hasStencil) this.capMode = "backface";
    this.clock = new DisplayClock(options.display ?? "strobe");

    const gl = renderer.getContext();
    const info = gl.getExtension("WEBGL_debug_renderer_info");
    this.gpu = String(gl.getParameter(info ? info.UNMASKED_RENDERER_WEBGL : gl.RENDERER));
    const lighting = options.lighting ?? "auto";
    this.lighting = lighting === "auto" ? (software ? "simple" : "studio") : lighting;

    if (this.lighting === "studio") {
      // Metals need something to reflect: a neutral room, filtered once.
      const pmrem = new PMREMGenerator(renderer);
      this.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
      pmrem.dispose();
      this.scene.environment = this.environment;
      this.scene.environmentIntensity = 0.6;
    }
    this.scene.background = new Color(options.background ?? BACKGROUND);
    this.scene.add(new HemisphereLight(0xffffff, 0x40444c, this.lighting === "studio" ? 0.9 : 1.6));
    this.key.position.set(1.2, 2.0, 1.6);
    this.fill.position.set(-1.5, 0.4, -1.0);
    this.scene.add(this.key, this.fill);
  }

  /** How bright a flat cap facing along `normal` is under the lights: one number per plane. */
  private readonly capShade = (normal: Vector3): number => {
    const key = Math.max(0, normal.dot(_dir.copy(this.key.position).normalize()));
    const fill = Math.max(0, normal.dot(_dir.copy(this.fill.position).normalize()));
    const up = 0.5 + 0.5 * normal.y;
    return 0.42 + 0.12 * up + 0.5 * key + 0.12 * fill;
  };

  /** Loads a glb (an ArrayBuffer or a URL) and binds it to a pose source. Replaces any model already loaded. */
  async load(glb: ArrayBuffer | string, pose: PoseSource): Promise<LoadedModel> {
    const buffer = typeof glb === "string" ? await (await fetch(glb)).arrayBuffer() : glb;
    const gltf = await new GLTFLoader().parseAsync(buffer, "");
    this.unload();
    const root = gltf.scene;
    this.scene.add(root);
    const parts = findParts(root);
    const binder = new PartBinder(root, pose.drivers);
    for (const u of binder.unbound) console.warn(`scene: joint ${u.node} has driver ${u.driver}, which the pose lacks`);
    root.updateMatrixWorld(true);
    const bounds = new Box3().setFromObject(root);
    if (this.lighting === "simple") {
      root.traverse((o) => {
        const material = (o as Mesh).material as MeshStandardMaterial | undefined;
        if (material?.isMeshStandardMaterial) material.metalness = Math.min(material.metalness, SIMPLE_METALNESS);
      });
    }
    const cutaway = new Cutaway(this.scene, parts, this.capMode, this.capShade);
    this.values = new Float64Array(pose.drivers.length);
    this.model = { root, parts, binder, cutaway, pose, bounds };
    return this.model;
  }

  unload(): void {
    const m = this.model;
    if (!m) return;
    m.cutaway.dispose();
    m.root.removeFromParent();
    m.root.traverse((o) => (o as { geometry?: { dispose(): void } }).geometry?.dispose());
    this.model = null;
  }

  setCamera(c: CameraPose): void {
    this.camera.position.set(c.position[0] ?? 0, c.position[1] ?? 0, c.position[2] ?? 0);
    this.target.set(c.target[0] ?? 0, c.target[1] ?? 0, c.target[2] ?? 0);
    this.camera.fov = c.fov;
    // Straight down (or up) the vertical: put the engine's front at the top of the picture.
    const dir = this.target.clone().sub(this.camera.position).normalize();
    if (Math.abs(dir.y) > 0.999) this.camera.up.set(0, 0, 1);
    else this.camera.up.set(0, 1, 0);
    this.camera.lookAt(this.target);
    this.camera.updateProjectionMatrix();
  }

  setCutaway(planes: readonly CutPlaneInput[]): void {
    this.model?.cutaway.setPlanes(planes);
  }

  setDisplay(mode: DisplayMode): void {
    this.clock.mode = mode;
  }

  resize(width: number, height: number): void {
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / Math.max(1, height);
    this.camera.updateProjectionMatrix();
  }

  /** Poses the model at exactly `angle` (crank radians), whatever the display mode. */
  pose(angle: number): void {
    const m = this.model;
    if (!m) return;
    m.pose.sample(angle, this.values);
    m.binder.apply(this.values);
  }

  /**
   * Draws one frame.
   * @param heard the crank angle being heard, radians
   * @param omega the crank speed, rad/s
   * @param now a time stamp in ms (performance.now() or the rAF time)
   */
  frame(heard: number, omega: number, now: number): FrameInfo {
    const dt = Number.isNaN(this.lastNow) ? 0 : Math.max(0, (now - this.lastNow) / 1000);
    this.lastNow = now;
    const angle = this.clock.advance(heard, omega, dt);
    this.pose(angle);
    return this.render(angle);
  }

  /** Renders the scene as it stands. */
  render(angle = this.clock.angle): FrameInfo {
    this.scene.updateMatrixWorld();
    this.model?.cutaway.update();
    this.renderer.render(this.scene, this.camera);
    const info = this.renderer.info.render;
    return { angle, calls: info.calls, triangles: info.triangles, caps: this.model?.cutaway.activeCaps ?? 0 };
  }

  /** The part under a point of the canvas, in normalised device coordinates (−1 to 1, y up). */
  pick(ndcX: number, ndcY: number): PickResult | null {
    const m = this.model;
    if (!m) return null;
    this.scene.updateMatrixWorld();
    this.raycaster.setFromCamera(new Vector2(ndcX, ndcY), this.camera);
    return m.cutaway.pick(this.raycaster);
  }

  dispose(): void {
    this.unload();
    this.environment?.dispose();
    this.renderer.dispose();
  }
}
